/*
 * Microsoft Graph token acquisition for the Outlook add-in.
 *
 * The add-in runs inside Outlook while the user is signed in, so it can broker a
 * Graph token off that live session via MSAL nested app authentication (NAA)
 * rather than going through the Salesforce MicrosoftAuth Auth Provider. That
 * removes the one-time "connect your Microsoft account in Salesforce" step and
 * the stale-token problem that came with it — the pane can re-mint on demand.
 */

/* global Office */

import {
  createNestablePublicClientApplication,
  InteractionRequiredAuthError,
  LogLevel,
  type IPublicClientApplication,
} from "@azure/msal-browser";

declare const MS_CLIENT_ID: string;

export interface TokenResult {
  accessToken: string;
  expiresOn: number; // epoch ms
}

/**
 * Minimum delegated scopes covering every Graph call in app.libraries:outlook.
 *
 * Calendars.Read.Shared is required by /me/calendar/getSchedule and
 * /me/findMeetingTimes. It is absent from the Salesforce Auth Provider's scope
 * list, so those two helpers never worked on the old path.
 */
export const GRAPH_SCOPES = [
  "User.Read",
  "Mail.ReadWrite",
  "Mail.Send",
  "Calendars.ReadWrite",
  "Calendars.Read.Shared",
  "Contacts.Read",
  "People.Read",
];

/** Re-acquire rather than hand out a token this close to expiry. */
const EXPIRY_SKEW_MS = 5 * 60 * 1000;

/** Assumed lifetime when MSAL does not report one. Conservative vs the usual ~60m. */
const DEFAULT_LIFETIME_MS = 55 * 60 * 1000;

type MsalFactory = () => Promise<IPublicClientApplication>;
type LogSink = (level: string, message: string) => void;

/**
 * Optional verbose sink. When the Outlook NAA broker rejects a request it can
 * return a bare Transient/PersistentError with no code and no description, so
 * MSAL's own log is the only place the underlying reason appears. Diagnostic
 * use only — left unset in normal builds.
 */
let logSink: LogSink | null = null;

export function setMsalLogSink(f: LogSink | null): void {
  logSink = f;
  msalInstance = null; // logger is fixed at construction time
}

let msalFactory: MsalFactory = async () =>
  createNestablePublicClientApplication({
    auth: { clientId: MS_CLIENT_ID, authority: "https://login.microsoftonline.com/common" },
    cache: { cacheLocation: "localStorage" },
    system: logSink
      ? {
          loggerOptions: {
            piiLoggingEnabled: false,
            logLevel: LogLevel.Verbose,
            loggerCallback: (level: LogLevel, message: string) => {
              logSink?.(LogLevel[level] ?? String(level), message);
            },
          },
        }
      : undefined,
  });

let msalInstance: IPublicClientApplication | null = null;
let cached: TokenResult | null = null;

/** Test seam — not used in production paths. */
export function __setMsalFactoryForTests(f: MsalFactory): void {
  msalFactory = f;
  msalInstance = null;
}

/** Test seam — not used in production paths. */
export function __resetTokenCacheForTests(): void {
  cached = null;
  msalInstance = null;
}

function isFresh(t: TokenResult | null): t is TokenResult {
  return !!t && t.expiresOn - Date.now() > EXPIRY_SKEW_MS;
}

/**
 * Interactive-only acquisition, used by the auth self-test to tell a broker
 * misconfiguration apart from a plain consent/interaction requirement.
 * Not part of any production path.
 */
export async function acquireTokenPopupForDiagnostic(): Promise<TokenResult> {
  if (!msalInstance) msalInstance = await msalFactory();
  const result: any = await msalInstance.acquireTokenPopup({ scopes: GRAPH_SCOPES });
  return {
    accessToken: result.accessToken,
    expiresOn: result.expiresOn
      ? new Date(result.expiresOn).getTime()
      : Date.now() + DEFAULT_LIFETIME_MS,
  };
}

/**
 * Get a Graph access token for the signed-in Outlook user.
 *
 * Silent first — the user is already authenticated to Outlook, so this normally
 * costs no UI. A popup only appears when Entra genuinely needs interaction (MFA,
 * password change, first consent).
 *
 * `force` is the backend's recovery path after a Graph 401 (revoked token,
 * changed consent, tenant policy): re-acquire rather than replay a dead token.
 */
export async function acquireGraphToken(force = false): Promise<TokenResult> {
  if (!force && isFresh(cached)) return cached;
  if (!isNaaSupported()) throw new Error("NAA not supported by this Outlook host");
  if (force) cached = null; // before the acquire — a failed retry must not leave it behind

  if (!msalInstance) msalInstance = await msalFactory();
  const request = { scopes: GRAPH_SCOPES, ...(force ? { forceRefresh: true } : {}) };

  let result: any;
  try {
    result = await msalInstance.acquireTokenSilent(request);
  } catch (err: any) {
    const interactive =
      err instanceof InteractionRequiredAuthError || err?.name === "InteractionRequiredAuthError";
    if (!interactive) throw err;
    result = await msalInstance.acquireTokenPopup(request);
  }

  cached = {
    accessToken: result.accessToken,
    expiresOn: result.expiresOn
      ? new Date(result.expiresOn).getTime()
      : Date.now() + DEFAULT_LIFETIME_MS,
  };
  return cached;
}

/**
 * Whether the running Office host can broker NAA tokens. Older Outlook builds
 * cannot; those users fall back to the Salesforce/MCP path server-side.
 */
export function isNaaSupported(): boolean {
  try {
    return !!Office?.context?.requirements?.isSetSupported("NestedAppAuth", "1.1");
  } catch {
    return false;
  }
}
